import type { FilterSidebarForm, BrandItemPair } from "./filter-sidebar-form";
import type { FilterSidebarHandler } from "./filter-sidebar-handler";

export class FilterSidebarListener {
    public constructor(
        private readonly form: FilterSidebarForm,
        private readonly handler: FilterSidebarHandler,
    ) {}

    public registerFilterChange(sidebar: HTMLElement): void {
        sidebar.addEventListener("change", (e) => {
            if (!(e.target instanceof HTMLInputElement) || e.target.type !== "checkbox") return;
            this.handler.navigate(this.handler.buildUrl(this.form));
        });
    }

    public registerClearAll(btn: HTMLElement): void {
        btn.addEventListener("click", () => {
            this.handler.navigate(this.handler.buildClearUrl());
        });
    }

    public registerKeywordSearch(
        input: HTMLInputElement,
        clearBtn: HTMLButtonElement | null,
        submitBtn: HTMLButtonElement | null,
    ): void {
        const syncClear = () => {
            if (clearBtn) clearBtn.hidden = !input.value;
        };
        const submit = () => {
            this.handler.navigate(this.handler.buildUrlForSearch(input.value));
        };
        syncClear();
        input.addEventListener("input", syncClear);
        input.addEventListener("keydown", (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                submit();
            }
        });
        if (submitBtn) {
            submitBtn.addEventListener("click", submit);
        }
        if (clearBtn) {
            // Only clears the textbox — doesn't navigate/re-run the search.
            clearBtn.addEventListener("click", () => {
                input.value = "";
                syncClear();
                input.focus();
            });
        }
    }

    public registerCategoryPicker(
        btn: HTMLButtonElement,
        panel: HTMLElement,
        items: HTMLElement[],
        label: HTMLElement,
        defaultLabel: string,
        toggleBtns: HTMLButtonElement[],
        filterInput: HTMLInputElement | null,
        groups: HTMLElement[],
        filterClearBtn: HTMLButtonElement | null,
    ): void {
        // Open / close the panel
        btn.addEventListener("click", (e) => {
            e.stopPropagation();
            const isOpen = panel.classList.toggle("is-open");
            btn.setAttribute("aria-expanded", String(isOpen));
        });

        // Accordion toggles inside the panel (expand children, don't navigate)
        toggleBtns.forEach((toggleBtn) => {
            toggleBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                const group = this.form.getPickerGroupOf(toggleBtn);
                if (!group) return;
                const children = this.form.getCatChildrenOf(group);
                if (!children) return;
                const isOpen = children.classList.toggle("is-open");
                toggleBtn.setAttribute("aria-expanded", String(isOpen));
            });
        });

        // Category filter input (live filter within picker panel)
        if (filterInput) {
            const syncFilterClear = () => {
                if (filterClearBtn) filterClearBtn.hidden = !filterInput.value;
            };
            syncFilterClear();
            filterInput.addEventListener("input", () => {
                this.handler.filterPickerCategories(filterInput.value, groups);
                syncFilterClear();
            });
            filterInput.addEventListener("keydown", (e) => {
                if (e.key === "Enter") e.preventDefault();
            });
            if (filterClearBtn) {
                filterClearBtn.addEventListener("click", () => {
                    filterInput.value = "";
                    syncFilterClear();
                    this.handler.filterPickerCategories("", groups);
                });
            }
        }

        // Select an item
        items.forEach((item) => {
            item.addEventListener("click", (e) => {
                const target = e.target as Element;
                // Accordion toggle never navigates
                if (this.form.isWithinCatToggle(target)) return;
                // Parent items: only navigate when clicking the name group (gap area suppressed)
                if (
                    item.dataset.filterParam === "parent_category_id" &&
                    !this.form.isWithinNameGroup(target)
                ) return;
                e.stopPropagation();

                const filterParam = item.dataset.filterParam ?? "none";
                const catId = item.dataset.catId ?? "";
                const catLabel = item.dataset.catLabel ?? "";

                items.forEach((i) => { i.classList.remove("is-active"); i.removeAttribute("aria-selected"); });
                item.classList.add("is-active");
                item.setAttribute("aria-selected", "true");

                label.textContent = catLabel || defaultLabel;

                this.handler.navigate(this.handler.buildUrlForCategorySelect(filterParam, catId));
            });
        });
    }

    public registerBrandSearch(searchInput: HTMLInputElement, pairs: BrandItemPair[]): void {
        searchInput.addEventListener("input", () => {
            this.handler.filterBrands(searchInput.value, pairs);
        });
        searchInput.addEventListener("keydown", (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                this.handler.navigate(this.handler.buildUrl(this.form));
            }
        });
    }

    public registerBrandToggle(btn: HTMLButtonElement, body: HTMLElement): void {
        btn.addEventListener("click", () => {
            const open = body.classList.toggle("is-open");
            btn.setAttribute("aria-expanded", String(open));
        });
    }
}
